import { useState, useEffect, useCallback } from "react";
import { useLoginModal } from "../components/LoginModalProvider";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { supabase } from "../lib/supabase";
import CancelBookingModal from "../components/CancelBookingModal";
import RescheduleBookingModal from "../components/RescheduleBookingModal";

/* ─── Design tokens (matches BookingsPage / OrderTrackingPage) ─── */
const T = {
  green:       "#0F7B3E",
  greenDark:   "#0C6231",
  greenTint:   "#E8F5E9",
  greenBd:     "#C8E6C9",
  pageBg:      "#F1F3F6",
  white:       "#FFFFFF",
  card:        "#FFFFFF",
  card2:       "#F8F9FB",
  border:      "#E8EAED",
  borderFaint: "#F0F2F5",
  text:        "#0D1117",
  textMid:     "#374151",
  sub:         "#6B7280",
  faint:       "#9CA3AF",
  ghost:       "#C4C9D4",
  amber:       "#D97706",
  blue:        "#1D4ED8",
  blueTint:    "#EFF6FF",
  blueBd:      "#BFDBFE",
  red:         "#DC2626",
  redTint:     "#FEF2F2",
  redBd:       "#FECACA",
  font: "'Inter', -apple-system, sans-serif",
  r8: "8px", r10: "10px", r12: "12px", r14: "14px",
  r16: "16px", r20: "20px", rFull: "999px",
  s1: "0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.03)",
  s2: "0 4px 16px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)",
  s3: "0 8px 32px rgba(0,0,0,0.09), 0 2px 8px rgba(0,0,0,0.05)",
  sGreen: "0 4px 20px rgba(15,123,62,0.22)",
};

const STATUS_CFG = {
  pending:              { label: "Pending",      color: T.blue,  bg: T.blueTint,  bd: T.blueBd  },
  pending_payment:      { label: "Pending",      color: T.blue,  bg: T.blueTint,  bd: T.blueBd  },
  confirmed:            { label: "Confirmed",    color: T.blue,  bg: T.blueTint,  bd: T.blueBd  },
  reschedule_requested: { label: "Reschedule Requested", color: T.amber, bg: "#FFFBEB", bd: "#FCD34D" },
  in_progress:          { label: "In Progress",  color: "#065F46", bg: "#ECFDF5", bd: "#A7F3D0" },
  completed:            { label: "Completed",    color: T.green, bg: T.greenTint, bd: T.greenBd },
  cancelled:            { label: "Cancelled",    color: T.red,   bg: T.redTint,  bd: T.redBd   },
};

/* ─── Atoms ─── */
function Ava({ name = "", avatar_url, size = 52 }) {
  const [err, setErr] = useState(false);
  const COLORS = ["#0F7B3E","#0369A1","#7C3AED","#B45309","#BE185D","#374151","#0E7490","#15803D"];
  const initials = name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const color = COLORS[Math.abs((name.charCodeAt(0)||0) + (name.charCodeAt(1)||0)) % COLORS.length];
  if (avatar_url && !err) {
    return (
      <div style={{ width: size, height: size, borderRadius: "50%", overflow: "hidden", flexShrink: 0, boxShadow: `0 0 0 2.5px #fff, 0 2px 10px ${color}44` }}>
        <img src={avatar_url} alt={name} onError={() => setErr(true)} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%", flexShrink: 0,
      background: `linear-gradient(145deg, ${color}EE 0%, ${color}88 100%)`,
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: Math.round(size * 0.32), fontWeight: 700, color: "#fff", fontFamily: T.font,
      boxShadow: `0 0 0 2.5px #fff, 0 2px 10px ${color}44`,
    }}>{initials || "?"}</div>
  );
}

function Pill({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: cfg.bg, border: `1px solid ${cfg.bd}`,
      borderRadius: T.rFull, padding: "5px 12px", flexShrink: 0,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: cfg.color }} />
      <span style={{ fontSize: 12, fontWeight: 700, color: cfg.color, fontFamily: T.font }}>{cfg.label}</span>
    </span>
  );
}

function Card({ children, style: sx = {} }) {
  return (
    <div style={{ background: T.card, borderRadius: T.r20, border: `1px solid ${T.border}`, boxShadow: T.s2, overflow: "hidden", ...sx }}>
      {children}
    </div>
  );
}

function Hr() { return <div style={{ height: 1, background: T.borderFaint }} />; }

function SHead({ title, sub }) {
  return (
    <div style={{ padding: "18px 22px 14px" }}>
      <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font, letterSpacing: "-0.01em" }}>{title}</p>
      {sub && <p style={{ margin: "2px 0 0", fontSize: 12, color: T.faint, fontFamily: T.font }}>{sub}</p>}
    </div>
  );
}

function Row({ label, value, valueColor, last }) {
  return (
    <>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "12px 22px", gap: 16 }}>
        <span style={{ fontSize: 13, color: T.sub, fontFamily: T.font, flexShrink: 0 }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: valueColor || T.text, fontFamily: T.font, textAlign: "right", maxWidth: 260 }}>{value}</span>
      </div>
      {!last && <Hr />}
    </>
  );
}

function Btn({ children, v = "ghost", size = "md", onClick, disabled, full }) {
  const [h, setH] = useState(false);
  const [p, setP] = useState(false);
  const { isMobile } = useBreakpoint();
  const sz = {
    sm: { fs: 12, h: 34, pad: "0 14px", r: T.r8 },
    md: { fs: 13, h: 40, pad: "0 18px", r: T.r10 },
    lg: { fs: 14, h: 48, pad: "0 24px", r: T.r12 },
  }[size];
  const vs = {
    primary: { bg: h ? T.greenDark : T.green, col: "#fff", bd: "none", sh: h ? T.sGreen : "0 2px 8px rgba(15,123,62,0.2)" },
    ghost:   { bg: h ? "#F3F5F8" : T.white, col: T.textMid, bd: `1px solid ${T.border}`, sh: "none" },
    danger:  { bg: h ? T.redTint : T.white, col: T.red, bd: `1px solid ${T.redBd}`, sh: "none" },
  };
  const s = vs[v] || vs.ghost;
  return (
    <button
      onClick={onClick} disabled={disabled}
      onMouseEnter={() => setH(true)} onMouseLeave={() => { setH(false); setP(false); }}
      onMouseDown={() => setP(true)} onMouseUp={() => setP(false)}
      style={{
        fontFamily: T.font, fontSize: sz.fs, fontWeight: 500, cursor: disabled ? "default" : "pointer",
        padding: sz.pad, height: sz.h, borderRadius: sz.r, border: s.bd,
        background: s.bg, color: s.col, boxShadow: s.sh,
        display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 7,
        whiteSpace: "nowrap", transition: "all 0.15s ease-out",
        transform: p ? "scale(0.97)" : "scale(1)",
        opacity: disabled ? 0.5 : 1, outline: "none",
        minHeight: isMobile ? 44 : "auto", width: full ? "100%" : "auto",
      }}>
      {children}
    </button>
  );
}

function LoadingState() {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ background: T.card, borderRadius: T.r16, border: `1px solid ${T.border}`, padding: isMobile ? "48px 16px" : "72px 24px", textAlign: "center", boxShadow: T.s2 }}>
      <div style={{ width: 40, height: 40, borderRadius: "50%", border: `3px solid ${T.greenBd}`, borderTopColor: T.green, animation: "spin 0.7s linear infinite", margin: "0 auto 16px" }} />
      <p style={{ margin: 0, fontSize: 14, color: T.faint, fontFamily: T.font }}>Loading booking…</p>
    </div>
  );
}

function ErrorState({ onRetry, onBack }) {
  const { isMobile } = useBreakpoint();
  return (
    <div style={{ background: T.card, borderRadius: T.r16, border: `1px solid ${T.redBd}`, padding: isMobile ? "48px 16px" : "72px 24px", textAlign: "center", boxShadow: T.s2 }}>
      <div style={{ width: 48, height: 48, borderRadius: "50%", background: T.redTint, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.red} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
      </div>
      <h3 style={{ margin: "0 0 8px", fontSize: 18, fontWeight: 700, color: T.text, fontFamily: T.font }}>Booking not found</h3>
      <p style={{ margin: "0 0 24px", fontSize: 14, color: T.sub, fontFamily: T.font, lineHeight: 1.65 }}>
        We couldn't load this booking. It may have been removed, or there was a connection issue.
      </p>
      <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
        <Btn v="ghost" size="md" onClick={onBack}>Back to bookings</Btn>
        <Btn v="primary" size="md" onClick={onRetry}>Try again</Btn>
      </div>
    </div>
  );
}

/* ─── Map raw row to UI shape ─── */
function mapBooking(b) {
  const proProfile = b.pro_profiles;
  const proUser    = proProfile?.profiles;
  return {
    id:               b.id,
    status:           b.status,
    service:          b.description || "Service",
    scheduled_time:   b.scheduled_time,
    requested_scheduled_time: b.requested_scheduled_time,
    location:         b.location || "—",
    estimated_price:  b.estimated_price || 0,
    payment_status:   b.payment_status || "unpaid",
    notes:            b.notes || "",
    created_at:       b.created_at,
    review:           b.reviews?.[0] || null,
    pro: {
      id:          b.pro_id,
      name:        proUser?.full_name || "JiraniPro",
      avatar_url:  proUser?.avatar_url || null,
      rating:      proProfile?.avg_rating ?? null,
      reviewCount: proProfile?.review_count ?? 0,
      verified:    proProfile?.verified ?? false,
    },
  };
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-KE", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
  catch { return iso; }
}
function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
}

/* ─────────────────────────────────────
   MAIN PAGE
───────────────────────────────────── */
export default function BookingDetailPage() {
  const navigate = useNavigate();
  const { open: openLogin } = useLoginModal();
  const { id } = useParams();
  const { user } = useAuth();
  const { isMobile, isTablet } = useBreakpoint();

  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCancel, setShowCancel] = useState(false);
  const [showReschedule, setShowReschedule] = useState(false);

  useEffect(() => {
    if (!user) openLogin();
  }, [user, navigate]);

  const fetchBooking = useCallback(async () => {
    if (!user?.id || !id) return;
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from("job_requests")
        .select(`
          id, status, scheduled_time, requested_scheduled_time, location,
          estimated_price, payment_status, description, notes, created_at, pro_id,
          pro_profiles ( id, avg_rating, review_count, verified, profiles!user_id ( full_name, avatar_url ) ),
          reviews ( rating, comment )
        `)
        .eq("id", id)
        .eq("user_id", user.id)
        .single();

      if (err) throw err;
      setBooking(mapBooking(data));
    } catch (err) {
      console.error("Booking detail fetch failed:", err);
      setError(err.message || "Failed to load booking");
    } finally {
      setLoading(false);
    }
  }, [id, user?.id]);

  useEffect(() => { fetchBooking(); }, [fetchBooking]);

  /* ── Message handler ── */
  const handleMessage = useCallback(async () => {
    if (!booking?.pro?.id || !user?.id) { navigate("/messages"); return; }
    try {
      const { data: existing } = await supabase
        .from("conversations")
        .select("id")
        .eq("customer_id", user.id)
        .eq("professional_id", booking.pro.id)
        .maybeSingle();

      if (existing?.id) { navigate(`/messages/${existing.id}`); return; }

      const { data: newConv, error: createErr } = await supabase
        .from("conversations")
        .insert({ job_request_id: booking.id, customer_id: user.id, professional_id: booking.pro.id })
        .select("id")
        .single();

      if (createErr) throw createErr;

      await supabase.from("conversation_participants").insert([
        { conversation_id: newConv.id, user_id: user.id },
        { conversation_id: newConv.id, user_id: booking.pro.id },
      ]);

      navigate(`/messages/${newConv.id}`);
    } catch (err) {
      console.error("Message conversation error:", err);
      navigate("/messages");
    }
  }, [navigate, user?.id, booking]);

  const mainPadding = isMobile ? "20px 16px" : (isTablet ? "24px 20px" : "28px 24px");

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: T.pageBg, fontFamily: T.font }}>
        <main style={{ maxWidth: 760, margin: "0 auto", padding: mainPadding }}>
          <LoadingState />
        </main>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !booking) {
    return (
      <div style={{ minHeight: "100vh", background: T.pageBg, fontFamily: T.font }}>
        <main style={{ maxWidth: 760, margin: "0 auto", padding: mainPadding }}>
          <ErrorState onRetry={fetchBooking} onBack={() => navigate("/bookings")} />
        </main>
      </div>
    );
  }

  const canModify = ["pending", "pending_payment", "confirmed"].includes(booking.status);
  const total = booking.estimated_price || 0;

  return (
    <div style={{ minHeight: "100vh", background: T.pageBg, fontFamily: T.font }}>
      <main style={{ maxWidth: 760, margin: "0 auto", padding: mainPadding, display: "flex", flexDirection: "column", gap: 14 }}>

        {/* Back + status */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <button onClick={() => navigate("/bookings")} style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "none", border: "none", cursor: "pointer", padding: 0,
            fontSize: 13, color: T.sub, fontFamily: T.font,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
            Back to bookings
          </button>
          <Pill status={booking.status} />
        </div>

        {/* Booking info */}
        <Card>
          <SHead title="Booking Details" sub={`Booking #${booking.id.slice(0, 8).toUpperCase()}`} />
          <Hr />
          <Row label="Service" value={booking.service} />
          <Row label="Date" value={fmtDate(booking.scheduled_time)} />
          <Row label="Time" value={fmtTime(booking.scheduled_time)} />
          <Row label="Address" value={booking.location} />
          <Row label="Payment" value={`Ksh ${total.toLocaleString()}`} valueColor={T.green} />
          <Row label="Payment status" value={booking.payment_status === "paid" ? "Paid" : "Unpaid"} last />
        </Card>

        {/* Pending reschedule notice */}
        {booking.status === "reschedule_requested" && booking.requested_scheduled_time && (
          <div style={{
            background: "#FFFBEB", border: "1px solid #FCD34D", borderRadius: T.r16,
            padding: "14px 18px", display: "flex", gap: 10, alignItems: "flex-start",
          }}>
            <span style={{ color: T.amber, flexShrink: 0, display: "flex", marginTop: 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            </span>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: T.amber, fontFamily: T.font }}>Reschedule requested</p>
              <p style={{ margin: "3px 0 0", fontSize: 12, color: "#92400E", fontFamily: T.font, lineHeight: 1.6 }}>
                You requested {fmtDate(booking.requested_scheduled_time)} at {fmtTime(booking.requested_scheduled_time)}.
                Waiting for your professional to confirm.
              </p>
            </div>
          </div>
        )}

        {/* Professional */}
        <Card>
          <SHead title="Your Professional" />
          <Hr />
          <div style={{ padding: "18px 22px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
            <Ava name={booking.pro.name} avatar_url={booking.pro.avatar_url} size={52} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: T.text, fontFamily: T.font }}>{booking.pro.name}</p>
              {booking.pro.rating != null && (
                <p style={{ margin: "3px 0 0", fontSize: 12, color: T.faint, fontFamily: T.font }}>
                  ★ {Number(booking.pro.rating).toFixed(1)} ({booking.pro.reviewCount} reviews)
                </p>
              )}
            </div>
            <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
              <Btn v="ghost" size="sm" onClick={handleMessage}>Message</Btn>
              {booking.pro.id && (
                <Btn v="ghost" size="sm" onClick={() => navigate(`/pro/${booking.pro.id}`)}>View Profile</Btn>
              )}
            </div>
          </div>
        </Card>

        {/* Notes */}
        {booking.notes && (
          <Card>
            <SHead title="Notes" />
            <Hr />
            <div style={{ padding: "16px 22px" }}>
              <p style={{ margin: 0, fontSize: 13, color: T.textMid, fontFamily: T.font, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{booking.notes}</p>
            </div>
          </Card>
        )}

        {/* Review (if completed) */}
        {booking.status === "completed" && (
          <Card>
            <SHead title="Your Review" />
            <Hr />
            <div style={{ padding: "16px 22px" }}>
              {booking.review ? (
                <p style={{ margin: 0, fontSize: 13, color: T.textMid, fontFamily: T.font, lineHeight: 1.7 }}>
                  You rated this service {booking.review.rating}/5{booking.review.comment ? ` — "${booking.review.comment}"` : ""}.
                </p>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <p style={{ margin: 0, fontSize: 13, color: T.sub, fontFamily: T.font }}>You haven't reviewed this booking yet.</p>
                  <Btn v="primary" size="sm" onClick={() => navigate(`/review/${booking.id}`)}>Leave a Review</Btn>
                </div>
              )}
            </div>
          </Card>
        )}

        {/* Actions */}
        <Card>
          <SHead title="Manage Booking" />
          <Hr />
          <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 10 }}>
            <Btn v="ghost" size="md" full onClick={() => navigate(`/bookings/${booking.id}/track`)}>
              Track Order
            </Btn>
            {canModify ? (
              <>
                <Btn v="ghost" size="md" full onClick={() => setShowReschedule(true)}>
                  Reschedule Booking
                </Btn>
                <Btn v="danger" size="md" full onClick={() => setShowCancel(true)}>
                  Cancel Booking
                </Btn>
              </>
            ) : (
              <p style={{ margin: 0, fontSize: 12, color: T.faint, fontFamily: T.font, textAlign: "center" }}>
                {booking.status === "reschedule_requested"
                  ? "This booking has a pending reschedule request and can't be modified further right now."
                  : "This booking can no longer be rescheduled or cancelled."}
              </p>
            )}
          </div>
        </Card>
      </main>

      {/* Modals */}
      {showCancel && (
        <CancelBookingModal
          booking={booking}
          onClose={() => setShowCancel(false)}
          onCancelled={(updated) => {
            setBooking(prev => ({ ...prev, status: "cancelled", notes: updated.notes }));
            setShowCancel(false);
          }}
        />
      )}
      {showReschedule && (
        <RescheduleBookingModal
          booking={booking}
          onClose={() => setShowReschedule(false)}
          onRequested={(updated) => {
            setBooking(prev => ({
              ...prev,
              status: "reschedule_requested",
              requested_scheduled_time: updated.requested_scheduled_time,
              notes: updated.notes,
            }));
            setShowReschedule(false);
          }}
        />
      )}

      <style>{`
        * { box-sizing: border-box; }
        @keyframes spin { to { transform: rotate(360deg); } }
        button:focus-visible { outline: 2px solid ${T.green}; outline-offset: 2px; }
      `}</style>
    </div>
  );
}